import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { 
  Layers, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  ScanBarcode,
  Upload,
  X
} from 'lucide-react';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';

export default function BatchScanner({ onBatchComplete }) {
  const { t, isSpanish } = useLanguage();
  const [barcodes, setBarcodes] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [bulkInput, setBulkInput] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);

  // Add single barcode
  const addBarcode = useCallback(() => {
    const code = inputValue.trim();
    if (code && !barcodes.includes(code)) {
      setBarcodes(prev => [...prev, code]);
      setInputValue('');
    }
  }, [inputValue, barcodes]);

  // Remove barcode from list
  const removeBarcode = useCallback((code) => {
    setBarcodes(prev => prev.filter(b => b !== code));
    setResults(prev => prev.filter(r => r.raw_code !== code));
  }, []);

  // Parse bulk input (one barcode per line or comma-separated)
  const parseBulkInput = useCallback(() => {
    const lines = bulkInput
      .split(/[\n,]/)
      .map(s => s.trim())
      .filter(s => s && s.length >= 4);
    
    const unique = [...new Set([...barcodes, ...lines])];
    setBarcodes(unique);
    setBulkInput('');
    setShowBulkInput(false);
  }, [bulkInput, barcodes]);

  // Scan all barcodes using batch endpoint
  const scanAllBarcodes = async () => {
    if (barcodes.length === 0) return;

    setScanning(true);
    setError(null);

    try {
      // Use batch endpoint for efficiency
      const response = await api.post('/barcode/batch-scan', {
        codes: barcodes,
        include_lookup: true
      });

      if (response.data.success) {
        setResults(response.data.results);
        
        // Notify parent if callback provided
        if (onBatchComplete) {
          onBatchComplete(response.data.results.filter(r => r.status === 'success'));
        }
      } else {
        setError(isSpanish ? 'Error al procesar lote' : 'Batch processing failed');
      }

    } catch (err) {
      console.error('Batch scan error:', err);
      setError(
        err.response?.data?.detail || 
        (isSpanish ? 'Error al procesar lote' : 'Batch processing error')
      );
    } finally {
      setScanning(false);
    }
  };

  // Clear all
  const clearAll = () => {
    setBarcodes([]);
    setResults([]);
    setError(null);
  };

  // Get result for a barcode
  const getResult = (code) => results.find(r => r.raw_code === code);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-blue-600" />
          {isSpanish ? 'Escaneo por Lotes' : 'Batch Barcode Scanning'}
        </CardTitle>
        <CardDescription>
          {isSpanish 
            ? 'Escanea múltiples códigos de barras a la vez'
            : 'Scan multiple barcodes at once for bulk product entry'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Input Section */}
        <div className="space-y-3">
          {/* Single Input */}
          <div className="flex gap-2">
            <Input
              placeholder={isSpanish ? 'Ingresa código de barras...' : 'Enter barcode...'}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addBarcode()}
              data-testid="batch-barcode-input"
            />
            <Button onClick={addBarcode} disabled={!inputValue.trim()}>
              <Plus className="w-4 h-4" />
            </Button>
          </div>

          {/* Bulk Input Toggle */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowBulkInput(!showBulkInput)}
            className="w-full"
          >
            <Upload className="w-4 h-4 mr-2" />
            {isSpanish ? 'Entrada Masiva (Copiar/Pegar)' : 'Bulk Input (Paste Multiple)'}
          </Button>

          {/* Bulk Input Area */}
          {showBulkInput && (
            <div className="space-y-2">
              <Textarea
                placeholder={isSpanish 
                  ? 'Pega códigos de barras (uno por línea o separados por comas)...'
                  : 'Paste barcodes (one per line or comma-separated)...'}
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                rows={4}
                data-testid="batch-bulk-input"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={parseBulkInput} disabled={!bulkInput.trim()}>
                  {isSpanish ? 'Agregar Todos' : 'Add All'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowBulkInput(false)}>
                  {isSpanish ? 'Cancelar' : 'Cancel'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Barcode Queue */}
        {barcodes.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">
                {isSpanish ? 'Cola de Códigos' : 'Barcode Queue'} ({barcodes.length})
              </span>
              <Button variant="ghost" size="sm" onClick={clearAll}>
                <Trash2 className="w-4 h-4 mr-1" />
                {isSpanish ? 'Limpiar' : 'Clear'}
              </Button>
            </div>

            <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
              {barcodes.map((code, index) => {
                const result = getResult(code);
                return (
                  <div 
                    key={code}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                    data-testid={`batch-item-${index}`}
                  >
                    <div className="flex items-center gap-2">
                      {result ? (
                        result.status === 'success' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-500" />
                        )
                      ) : (
                        <ScanBarcode className="w-4 h-4 text-gray-400" />
                      )}
                      <code className="text-sm font-mono">{code}</code>
                      {result?.product_name && (
                        <span className="text-sm text-gray-500">
                          - {result.product_name}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {result?.format && (
                        <Badge variant="outline" className="text-xs">
                          {result.format}
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => removeBarcode(code)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={scanAllBarcodes}
            disabled={barcodes.length === 0 || scanning}
            className="flex-1"
            data-testid="batch-scan-all"
          >
            {scanning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isSpanish ? 'Escaneando...' : 'Scanning...'}
              </>
            ) : (
              <>
                <ScanBarcode className="w-4 h-4 mr-2" />
                {isSpanish 
                  ? `Escanear Todos (${barcodes.length})`
                  : `Scan All (${barcodes.length})`}
              </>
            )}
          </Button>
        </div>

        {/* Results Summary */}
        {results.length > 0 && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {isSpanish ? 'Resultados' : 'Results'}:
              </span>
              <div className="flex gap-3">
                <span className="text-green-600">
                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                  {results.filter(r => r.status === 'success').length} {isSpanish ? 'éxito' : 'success'}
                </span>
                <span className="text-red-600">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {results.filter(r => r.status === 'failed').length} {isSpanish ? 'fallido' : 'failed'}
                </span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
